import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Trash2,
  RotateCcw,
  Search,
  UserX,
  FilterX,
  AlertTriangle,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "@/hooks/use-auth";
import { AccessDenied } from "@/components/auth/AccessDenied";
import { TableSkeleton } from "@/components/loading";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  fetchDeletedUsers,
  restoreUser,
  bulkRestoreUsers,
  type DeletedUserRow,
  type DeletedUsersFilterQuery,
} from "@/lib/api/deleted-users";

export const Route = createFileRoute("/_authenticated/admin/deleted-users")({
  component: DeletedUsersPage,
});

const PAGE_SIZE = 25;

function DeletedUsersPage() {
  const { isAdmin, isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();

  // ── State ──
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Restore dialog
  const [restoreTarget, setRestoreTarget] = useState<DeletedUserRow | null>(null);
  const [bulkRestoreOpen, setBulkRestoreOpen] = useState(false);

  if (!isAdmin) {
    return <AccessDenied message="You don't have permission to manage deleted users." />;
  }

  // ── Query ──
  const queryParams: DeletedUsersFilterQuery = useMemo(
    () => ({
      pageNumber: page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      role: roleFilter !== "all" ? roleFilter : undefined,
      sortBy: "deletedat",
      sortDesc: true,
    }),
    [page, search, roleFilter],
  );

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "deleted-users", queryParams],
    queryFn: () => fetchDeletedUsers(queryParams),
  });

  const users = data?.items ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;

  // ── Mutations ──
  const restoreMutation = useMutation({
    mutationFn: (userId: string) => restoreUser(userId),
    onSuccess: () => {
      toast.success("User restored successfully.");
      queryClient.invalidateQueries({ queryKey: ["admin", "deleted-users"] });
      setRestoreTarget(null);
    },
    onError: (err: any) => {
      const msg = err?.data?.error || err?.message || "Failed to restore user.";
      toast.error(msg);
    },
  });

  const bulkRestoreMutation = useMutation({
    mutationFn: (userIds: string[]) => bulkRestoreUsers(userIds),
    onSuccess: (result) => {
      if (result.failureCount > 0) {
        toast.warning(
          `Restored ${result.successCount} user(s), ${result.failureCount} failed.`,
        );
        // Show individual errors
        result.results
          .filter((r) => !r.success && r.error)
          .forEach((r) => toast.error(r.error));
      } else {
        toast.success(`Successfully restored ${result.successCount} user(s).`);
      }
      queryClient.invalidateQueries({ queryKey: ["admin", "deleted-users"] });
      setBulkRestoreOpen(false);
      setSelected(new Set());
    },
    onError: (err: any) => {
      const msg = err?.data?.error || err?.message || "Bulk restore failed.";
      toast.error(msg);
    },
  });

  // ── Selection helpers ──
  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === users.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(users.map((u) => u.id)));
    }
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function clearFilters() {
    setSearch("");
    setRoleFilter("all");
    setPage(1);
  }

  const hasActiveFilters = !!search || roleFilter !== "all";

  function formatDate(dateStr: string | null) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-muted-foreground" />
            Deleted Users
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage soft-deleted user accounts.
            {totalCount > 0 && (
              <span className="ml-2 font-medium">
                {totalCount} deleted account{totalCount !== 1 ? "s" : ""}
              </span>
            )}
          </p>
        </div>
        {selected.size > 0 && isSuperAdmin && (
          <Button
            variant="default"
            onClick={() => setBulkRestoreOpen(true)}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Restore {selected.size} User{selected.size !== 1 ? "s" : ""}
          </Button>
        )}
      </div>

      {!isSuperAdmin && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span>
            Only Super Administrators can restore deleted users. You can view the list but
            cannot perform restore actions.
          </span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or username…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="SuperAdmin">Super Admin</SelectItem>
            <SelectItem value="SystemAdmin">System Admin</SelectItem>
            <SelectItem value="TeamLeader">Team Leader</SelectItem>
            <SelectItem value="ContentCreator">Content Creator</SelectItem>
            <SelectItem value="Student">Student</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
            <FilterX className="h-4 w-4" />
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={5} columns={7} />
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mb-3" />
          <p className="text-lg font-semibold text-foreground">Failed to load deleted users</p>
          <p className="text-sm text-muted-foreground">Please try again later.</p>
        </div>
      ) : users.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <UserX className="h-16 w-16 text-muted-foreground/40 mb-4" />
          <p className="text-lg font-semibold text-foreground">No deleted accounts</p>
          <p className="text-sm text-muted-foreground max-w-md">
            {hasActiveFilters
              ? "No deleted users match your current filters."
              : "There are currently no soft-deleted user accounts."}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {isSuperAdmin && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selected.size === users.length && users.length > 0}
                        onCheckedChange={toggleAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Deleted Date</TableHead>
                  <TableHead>Status</TableHead>
                  {isSuperAdmin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="opacity-75 hover:opacity-100 transition-opacity">
                    {isSuperAdmin && (
                      <TableCell>
                        <Checkbox
                          checked={selected.has(user.id)}
                          onCheckedChange={() => toggleSelect(user.id)}
                          aria-label={`Select ${user.fullName || user.email}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium">
                      {user.fullName || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {user.userName || "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.roles.length > 0 ? (
                          user.roles.map((role) => (
                            <Badge key={role} variant="outline" className="text-xs">
                              {role}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(user.deletedAt)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="destructive"
                        className="gap-1"
                      >
                        <Trash2 className="h-3 w-3" />
                        Deleted
                      </Badge>
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => setRestoreTarget(user)}
                          disabled={restoreMutation.isPending}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          Restore
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className={page <= 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        onClick={() => setPage(pageNum)}
                        isActive={page === pageNum}
                        className="cursor-pointer"
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className={page >= totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </>
      )}

      {/* Single Restore Dialog */}
      <Dialog open={!!restoreTarget} onOpenChange={(open) => { if (!open) setRestoreTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Restore Deleted User</DialogTitle>
            <DialogDescription>
              This will restore the account for{" "}
              <span className="font-semibold text-foreground">
                {restoreTarget?.fullName || restoreTarget?.email}
              </span>
              . The user will become active immediately with all previous roles, enrollments, and
              permissions intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (restoreTarget) restoreMutation.mutate(restoreTarget.id);
              }}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? "Restoring…" : "Confirm Restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Restore Dialog */}
      <Dialog open={bulkRestoreOpen} onOpenChange={(open) => { if (!open) setBulkRestoreOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Restore Users</DialogTitle>
            <DialogDescription>
              You are about to restore{" "}
              <span className="font-semibold text-foreground">{selected.size}</span> deleted
              user account{selected.size !== 1 ? "s" : ""}. If any user has an email or
              username conflict with an active account, that individual restore will fail with
              a clear error.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkRestoreOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkRestoreMutation.mutate(Array.from(selected))}
              disabled={bulkRestoreMutation.isPending}
            >
              {bulkRestoreMutation.isPending
                ? "Restoring…"
                : `Restore ${selected.size} User${selected.size !== 1 ? "s" : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
